import classnames from "classnames";
import React, { ReactNode } from "react";

import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { Button } from "@components/General/Button/button.component";
import { ModalFooter } from "@components/General/Modal/footer.component";

import { ModalBody } from "../Modal/body.component";
import { ModalHeader } from "../Modal/header.component";
import { Modal } from "../Modal/modal.component";

type Props = {
    title: string;
    customTitle?: (title: string) => ReactNode;
    subtitle?: string;
    children?: any;
    size?: "sm" | "lg";
    confirmButtonText?: any;
    confirmButtonColor?:
        | "primary"
        | "secondary"
        | "gray"
        | "link"
        | "transparent"
        | "danger";
    confirmHandler?: any;
    cancelHandler?: any;
    onOpenedHandler?: any;
    modalState: boolean;
    toggleHandler?: (newState: boolean) => void;
    closable?: boolean;
    formId?: string;
    isLoading?: boolean;
    isConfirmDisabled?: boolean;
    withFooter?: boolean;
    centered?: boolean;
    backdrop?: boolean;
    autoFocus?: boolean;
    scrollable?: boolean;
    className?: string;
};

const HuboModal = ({
    title,
    customTitle,
    subtitle,
    children,
    size = "lg",
    confirmButtonText,
    confirmHandler,
    cancelHandler,
    onOpenedHandler,
    modalState,
    toggleHandler,
    formId,
    isLoading,
    isConfirmDisabled = false,
    withFooter = true,
    backdrop = true,
    autoFocus = true,
    closable = true,
    centered = true,
    confirmButtonColor = "secondary",
    scrollable = false,
    className,
}: Props) => {
    const { t } = useGranite();

    const closeButton = () => (
        <button
            className="modal-close close m-0"
            onClick={() => {
                toggleHandler && toggleHandler(false);
                cancelHandler && cancelHandler();
            }}
            aria-label={t("accessibility.modal.close")}
        >
            <i className="las la-times" />
        </button>
    );

    return (
        <Modal
            className={className}
            isOpen={modalState}
            toggle={
                closable
                    ? () => {
                          toggleHandler && toggleHandler(!modalState);
                          modalState && cancelHandler && cancelHandler();
                      }
                    : undefined
            }
            centered={centered}
            size={size}
            autoFocus={autoFocus}
            backdrop={backdrop}
            onOpened={() => {
                onOpenedHandler && onOpenedHandler();
            }}
            contentClassName={"p-3 p-md-4"}
            scrollable={scrollable}
        >
            <ModalHeader
                tag={customTitle ? "div" : "h2"}
                toggle={
                    closable
                        ? () => {
                              toggleHandler && toggleHandler(!modalState);
                              modalState && cancelHandler && cancelHandler();
                          }
                        : undefined
                }
                close={closable ? closeButton() : undefined}
                className={"border-0"}
            >
                {customTitle ? customTitle(title) : <>{title}</>}
                {subtitle && <div>{subtitle}</div>}
            </ModalHeader>
            <ModalBody className="p-0">{children}</ModalBody>
            {withFooter && confirmButtonText && (
                <ModalFooter className="p-0 b-0">
                    <div
                        className={classnames(
                            "d-flex w-100 m-0 mt-2 mt-sm-3 align-items-center",
                            closable
                                ? "justify-content-between"
                                : "justify-content-end",
                        )}
                    >
                        {closable && (
                            <Button
                                color="link"
                                type="button"
                                className={"px-0"}
                                onClick={() => {
                                    toggleHandler && toggleHandler(false);
                                    cancelHandler && cancelHandler();
                                }}
                            >
                                <i className="las la-angle-left" />
                                {t("general.button.cancel")}
                            </Button>
                        )}
                        <Button
                            type={formId ? "submit" : "button"}
                            form={formId || ""}
                            color={confirmButtonColor}
                            onClick={() => confirmHandler && confirmHandler()}
                            loading={isLoading}
                            disabled={isConfirmDisabled}
                        >
                            {confirmButtonText}
                        </Button>
                    </div>
                </ModalFooter>
            )}
        </Modal>
    );
};

export { HuboModal };
